import { getToken, setToken } from './token';
import { refreshSession } from './refresh';
import { setConnectionOnline } from './connection';
import { clearSessionUser } from './offline/cache';
import { recordNetworkFail } from './diagnostics/networkBuffer';
import { dbUnavailableAction, DB_UNAVAILABLE, DB_UNAVAILABLE_SAVE_MESSAGE, DB_UNAVAILABLE_READ_MESSAGE, DB_UNAVAILABLE_WRITE_EVENT } from './dbUnavailable';
import type { ApiResponse, ApiError } from '@avsuite/shared';

const TENANT: string = (import.meta.env.VITE_TENANT as string | undefined) ?? 'flightstandards';

// Step 5d Phase 1: a network-LEVEL fetch failure (rejection — the server never answered)
// is thrown as this typed error so callers can route it to the offline read path. HTTP
// error RESPONSES (4xx/5xx, incl. the vite proxy's 500 when the backend is down in dev)
// are NOT NetworkErrors — they come back in the envelope and keep their existing handling.
export class NetworkError extends Error {
  constructor(message = 'Network unavailable — the server could not be reached.') {
    super(message);
    this.name = 'NetworkError';
  }
}

// 0.12.11 Part B: the backend reached, but its DATABASE did not (503 + code DB_UNAVAILABLE; the
// decision is lib/dbUnavailable.ts). A READ that got it is thrown as a NetworkError subclass, so
// every caller that already routes NetworkError to the offline read path (the board's cached window
// + data-age banner) takes that path unchanged. It does NOT flip the connection store to offline:
// the server answered, so the "Backend unreachable" banner would be false, and the offline→online
// flap on the next good response would re-fire the op-queue drain and the rolling prefetch for nothing.
export class DbUnavailableError extends NetworkError {
  constructor() {
    super(DB_UNAVAILABLE_READ_MESSAGE);
    this.name = 'DbUnavailableError';
  }
}

// Typed fetch wrapper. Handles auth headers, 401 silent-refresh + retry, and the
// standard { data, error, warnings } response envelope.
//
// Step 5c retrofit (closes the 4.5b deferral): the runtime now returns the FULL
// ApiResponse<T> envelope — callers receive `warnings` (the bookings API delivers
// advisory prompts there) and inspect `error` rather than relying on a throw.
// The 401 → silent-refresh → retry behaviour is unchanged. Auth endpoints are NOT
// retrofitted (they keep the legacy shape; AuthContext calls them via raw fetch).
//
// 5d-p2: the envelope additionally carries `status` (and `retryAfterMs` on a 429) so
// the offline op-queue drain can back off on rate limits without re-implementing the
// auth/refresh plumbing. Existing callers ignore the extra fields.
export interface ApiResponseMeta {
  status?: number;
  retryAfterMs?: number;
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T> & ApiResponseMeta> {
  // Problem-reporter diagnostics: stamp start + method so non-2xx / network throws can be
  // recorded into the bounded network-fail buffer (used by a problem report).
  const startedAt = Date.now();
  const method = (options.method ?? 'GET').toString().toUpperCase();

  const makeRequest = (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant': TENANT,
      ...(options.headers as Record<string, string> | undefined),
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(path, { ...options, credentials: 'include', headers });
  };

  // A reached backend (ANY HTTP status) clears the offline banner; a network-level throw
  // (backend unreachable — fetch rejects with a TypeError) raises it. (Part C.)
  const send = async (token: string | null): Promise<Response> => {
    try {
      const r = await makeRequest(token);
      setConnectionOnline(true);
      return r;
    } catch (e) {
      setConnectionOnline(false);
      // AbortErrors are caller-initiated (superseded refetch), not connectivity — rethrow as-is.
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      recordNetworkFail({ t: new Date().toISOString(), method, path, status: 0, ms: Date.now() - startedAt });
      throw new NetworkError();
    }
  };

  let res = await send(getToken());

  if (res.status === 401) {
    // Single-flight: a burst of concurrent 401s shares ONE /api/auth/refresh (see refresh.ts).
    const refreshed = await refreshSession();
    if (refreshed.ok) {
      setToken(refreshed.result.access_token);
      res = await send(refreshed.result.access_token);
    } else if (refreshed.reason === 'rejected') {
      // Explicit 401/403 from the refresh endpoint — the session really is over.
      setToken(null);
      void clearSessionUser();
      window.location.href = '/login';
      throw new Error('Session expired.');
    } else {
      // Refresh unavailable (connection dropped mid-sequence / backend died): do NOT log
      // out — surface as a network failure so the caller can use the offline read path.
      throw new NetworkError('Session could not be refreshed — server unreachable.');
    }
  }

  // Record the FINAL outcome only: a 401 that silently refreshed → 200 is not a failure, so
  // this runs after the 401-retry block and fires solely on a genuine non-2xx response.
  if (!res.ok) {
    recordNetworkFail({ t: new Date().toISOString(), method, path, status: res.status, ms: Date.now() - startedAt });
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  const obj = (body && typeof body === 'object' ? (body as Record<string, unknown>) : {});

  const data = ('data' in obj ? (obj.data as T | null) : null);

  // Normalise the error field: route()-wrapped handlers emit an ApiError object;
  // the legacy auth/middleware paths emit a plain string. Either becomes ApiError.
  const rawError = 'error' in obj ? obj.error : null;
  const error: ApiError | null =
    rawError == null
      ? null
      : typeof rawError === 'string'
        ? { message: rawError }
        : (rawError as ApiError);

  const warnings: string[] = Array.isArray(obj.warnings) ? (obj.warnings as string[]) : [];

  // Retry-After (seconds) — set by the rate limiter's standardHeaders on a 429.
  const retryAfterRaw = res.status === 429 ? Number(res.headers.get('Retry-After')) : NaN;
  const meta: ApiResponseMeta = {
    status: res.status,
    ...(Number.isFinite(retryAfterRaw) && retryAfterRaw > 0 ? { retryAfterMs: retryAfterRaw * 1000 } : {}),
  };

  // 0.12.11 Part B. The code arrives as error.code (route()'s typed envelope) or as a top-level
  // `code` (the global handler's legacy { data, error: string } shape).
  const dbAction = dbUnavailableAction(res.status, method, error?.code, obj.code);
  // A read → the offline read path, exactly as a network failure.
  if (dbAction === 'offline-read') throw new DbUnavailableError();
  // A write → NOT thrown, so no caller's `catch (NetworkError)` queues it for offline replay; it
  // comes back as an ordinary error (callers keep their form open), never as a save, never retried.
  // Its outcome is UNKNOWN (the connection may have dropped during the COMMIT), so the message says
  // it could not be confirmed, and the board is told to refresh itself (lib/dbUnavailable.ts).
  if (dbAction === 'write-failed') {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(DB_UNAVAILABLE_WRITE_EVENT));
    return { data: null, error: { message: DB_UNAVAILABLE_SAVE_MESSAGE, code: DB_UNAVAILABLE }, warnings: [], ...meta };
  }

  // Synthesise an error for non-2xx responses that didn't carry one (defensive).
  if (!res.ok && !error) {
    return { data: null, error: { message: `Request failed (${res.status}).` }, warnings, ...meta };
  }

  return { data, error, warnings, ...meta };
}
